/**
 * Pure helpers for the Historia tab.
 *
 * As in the Dead letter tab, the wire shape is described structurally so a change in
 * `api/dod/dod.v2.types` lands in one mapper rather than across presenters and tests.
 */

import { dodReaderLabel } from '../overviewTab/dodFormat';

export type HistoryStatus = 'OK' | 'ERROR';

export type HistorySource = 'direct' | 'dod';

export type HistoryWireEntry = {
  timestamp: number;
  /** The RCP's name as recorded; the queue tab and the URL filter use its id (`rcpUuid`). */
  rcpId: string;
  rcpUuid?: string;
  nlk: number;
  commandType: string;
  commandName: string;
  sequenceNumber: number;
  status: HistoryStatus;
  errorMessage?: string;
  durationMs: number;
  source?: HistorySource;
  rcpNazwa?: string;
  tkdNazwa?: string;
  /** Raw request frame as it went on the wire; absent on a backend that does not record it. */
  frame?: string | null;
  /** Controller response, verbatim. */
  response?: string | null;
  /** Pod that executed the order. */
  podId?: string | null;
};

export const HISTORY_PAGE_SIZES = [50, 100, 200];

export type HistoryRow = {
  /** Synthesised: the wire entry carries no identifier and the shared Table keys rows by `id`. */
  id: string;
  timestamp: number;
  rcpId: string;
  rcpLabel: string;
  nlk: number;
  tkdLabel: string;
  commandType: string;
  commandName: string;
  sequenceNumber: number;
  status: HistoryStatus;
  errorMessage: string;
  durationMs: number;
  source: HistorySource;
  frame: string;
  response: string;
  podId: string;
  raw: HistoryWireEntry;
};

/**
 * Names the backend recorded before orders 17 and 18 got their protocol names (WKD-2513). History
 * rows are stored with the name they were written with, so rows from before the rename would keep
 * showing the old one; they are shown under the current name instead.
 */
const RENAMED_COMMAND_NAMES: Readonly<Record<string, string>> = {
  USTAW_MOD_RCP: 'USTAW_HARMONOGRAM_RCP',
  USTAW_STREFE_PRZEJSCIA: 'USTAW_HARMONOGRAM_PRZEJSCIA',
};

export const historyCommandName = (commandName: string): string => RENAMED_COMMAND_NAMES[commandName] ?? commandName;

/** Stable enough to key a React list and to dedupe the socket feed against a fetched page. */
export const historyRowId = (entry: HistoryWireEntry): string =>
  [entry.timestamp, entry.rcpId, entry.nlk, entry.sequenceNumber, entry.commandType].join(':');

export const toHistoryRow = (entry: HistoryWireEntry): HistoryRow => ({
  id: historyRowId(entry),
  timestamp: entry.timestamp,
  rcpId: entry.rcpId,
  rcpLabel: entry.rcpNazwa ?? entry.rcpId,
  nlk: entry.nlk,
  tkdLabel: dodReaderLabel(entry.tkdNazwa, entry.nlk),
  commandType: entry.commandType,
  commandName: historyCommandName(entry.commandName),
  sequenceNumber: entry.sequenceNumber,
  status: entry.status,
  errorMessage: entry.errorMessage ?? '',
  durationMs: entry.durationMs,
  source: entry.source ?? 'direct',
  frame: entry.frame ?? '',
  response: entry.response ?? '',
  podId: entry.podId ?? '',
  raw: entry,
});

export const formatClockTimeWithMs = (timestamp: number): string => {
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  const pad = (value: number, length = 2): string => String(value).padStart(length, '0');

  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(
    date.getMilliseconds(),
    3,
  )}`;
};

export type HistoryQueryFilter = {
  rcpId?: string;
  nlk?: number;
  command?: string;
  status?: HistoryStatus;
  source?: HistorySource;
  from?: number;
  to?: number;
  search?: string;
};

export type HistoryFilterInput = {
  shared: { rcp: string; nlk: string; cmd: string; status: string };
  fromTs: number;
  toTs: number;
  source: HistorySource | '';
  status: HistoryStatus | '';
  search: string;
};

const parseNlk = (value: string): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * The tab-local status select wins over the shared one: it is the control the user just touched,
 * and the toolbar's status filter is shared with tabs that use a different status vocabulary.
 */
export const toHistoryQueryFilter = (input: HistoryFilterInput): HistoryQueryFilter => {
  const filter: HistoryQueryFilter = { from: input.fromTs, to: input.toTs };

  if (input.shared.rcp) {
    filter.rcpId = input.shared.rcp;
  }

  const nlk = parseNlk(input.shared.nlk);
  if (nlk !== undefined) {
    filter.nlk = nlk;
  }

  if (input.shared.cmd) {
    filter.command = input.shared.cmd;
  }

  const status =
    input.status || (input.shared.status === 'OK' || input.shared.status === 'ERROR' ? input.shared.status : '');
  if (status) {
    filter.status = status;
  }

  if (input.source) {
    filter.source = input.source;
  }

  if (input.search.trim()) {
    filter.search = input.search.trim();
  }

  return filter;
};

/** Serialises the filter for `GET /dod/history?...&format=csv`. Undefined members are dropped. */
export const historyCsvQueryString = (filter: HistoryQueryFilter): string => {
  const params = new URLSearchParams();

  Object.entries(filter).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });

  params.set('format', 'csv');

  return params.toString();
};

/**
 * Does a socket entry belong to the view the user is looking at?
 *
 * The live feed is unfiltered, so an entry that the current filters exclude must not be counted as
 * "new" — a counter that ticks for rows the user would never see is worse than no counter.
 */
export const matchesHistoryFilter = (
  entry: HistoryWireEntry,
  filter: HistoryQueryFilter,
  rcpAliases?: ReadonlySet<string>,
): boolean => {
  if (filter.rcpId) {
    const accepted = rcpAliases ?? new Set([filter.rcpId]);

    if (!accepted.has(entry.rcpId) && !(entry.rcpUuid && accepted.has(entry.rcpUuid))) {
      return false;
    }
  }

  if (filter.nlk !== undefined && entry.nlk !== filter.nlk) {
    return false;
  }

  if (filter.command && normalizeCommandId(entry.commandType) !== normalizeCommandId(filter.command)) {
    return false;
  }

  if (filter.status && entry.status !== filter.status) {
    return false;
  }

  if (filter.source && (entry.source ?? 'direct') !== filter.source) {
    return false;
  }

  if (filter.search) {
    const needle = filter.search.toLowerCase();
    const haystack = `${entry.commandName} ${entry.errorMessage ?? ''}`.toLowerCase();

    if (!haystack.includes(needle)) {
      return false;
    }
  }

  return true;
};

/**
 * Entries that arrived over the socket since the last flush and are not already on the page.
 *
 * Returned rather than spliced into the table: the design requires the table to stay still while
 * a counter offers "N new — show", so the caller holds this list until the user asks for it.
 */
export const pendingHistoryEntries = (
  liveEntries: HistoryWireEntry[],
  filter: HistoryQueryFilter,
  shownIds: Set<string>,
  since: number,
  rcpAliases?: ReadonlySet<string>,
): HistoryWireEntry[] =>
  liveEntries.filter(
    (entry) =>
      entry.timestamp > since && !shownIds.has(historyRowId(entry)) && matchesHistoryFilter(entry, filter, rcpAliases),
  );

/** '03', '3' and 3 are the same command (mirrors the backend's normalizeCommandId). */
const normalizeCommandId = (value: string): string => {
  const text = value.trim();
  const stripped = text.replace(/^0+/, '');

  return stripped === '' ? (text === '' ? '' : '0') : stripped;
};

export type HistoryFleetReader = { rcpId: string; rcpNazwa?: string; nlk: number; tkdNazwa?: string };

/**
 * The RCP filter value (an id, as the queue tab and its "Show in history" link write it) plus the
 * name it stands for: history entries recorded before 2026-09-25 carry only the name.
 */
export const historyRcpAliases = (value: string, readers: HistoryFleetReader[]): Set<string> => {
  const aliases = new Set([value]);

  readers.forEach((reader) => {
    if (reader.rcpId === value && reader.rcpNazwa) {
      aliases.add(reader.rcpNazwa);
    }

    if (reader.rcpNazwa === value) {
      aliases.add(reader.rcpId);
    }
  });

  return aliases;
};

type SelectOption = { label: string; value: string };

/**
 * Toolbar options for the history tab, built from the whole fleet rather than from the rows on
 * screen: rows are already filtered, so a select built from them could only offer what the current
 * filter shows (STG: with NLK 64 selected the TKD select offered nothing else). The TKD label
 * carries the NLK because reader names repeat across controllers, and a selected value that is not
 * in the fleet is kept so the select never renders blank.
 */
export const toHistoryToolbarOptions = (
  readers: HistoryFleetReader[],
  selected: { rcp: string; nlk: string },
): { rcp: SelectOption[]; tkd: SelectOption[] } => {
  const rcpById = new Map<string, string>();
  readers.forEach((reader) => rcpById.has(reader.rcpId) || rcpById.set(reader.rcpId, reader.rcpNazwa ?? reader.rcpId));

  const inRcp = selected.rcp
    ? readers.filter((reader) => reader.rcpId === selected.rcp || reader.rcpNazwa === selected.rcp)
    : readers;
  const rcpCount = new Set(inRcp.map((reader) => reader.rcpId)).size;
  const tkdByNlk = new Map<string, string>();

  inRcp.forEach((reader) => {
    const value = String(reader.nlk);
    const name = dodReaderLabel(reader.tkdNazwa, reader.nlk);
    const label = rcpCount > 1 ? `${reader.rcpNazwa ?? reader.rcpId} · ${name}` : name;

    tkdByNlk.set(value, tkdByNlk.has(value) ? `${tkdByNlk.get(value)}, ${label}` : label);
  });

  if (selected.rcp && !rcpById.has(selected.rcp)) {
    rcpById.set(selected.rcp, selected.rcp);
  }

  if (selected.nlk && !tkdByNlk.has(selected.nlk)) {
    tkdByNlk.set(selected.nlk, `NLK ${selected.nlk}`);
  }

  return {
    rcp: Array.from(rcpById, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label)),
    tkd: Array.from(tkdByNlk, ([value, label]) => ({ value, label })).sort((a, b) => Number(a.value) - Number(b.value)),
  };
};
